/*
Copyright © 2021 EPAM Systems, Inc. All Rights Reserved.
All information contained herein is, and remains the property of EPAM Systems, Inc.
and/or its suppliers and is protected by international intellectual property law.
Dissemination of this information or reproduction of this material is strictly
forbidden, unless prior written permission is obtained from EPAM Systems, Inc
*/
import React, { useEffect, useRef, useState } from 'react';
import { findDOMNode } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { DOC_DOMAIN } from '../../clinicsData';
import { Link } from '../../components/Link/Link';
import { GroupInput } from '../../components/GroupInput/GroupInput';
import { FormTitle } from '../../components/FormTitle/FormTitle';
import { FormGroup } from '../../components/FormGroup/FormGroup';
import { formHocWithFormName } from '../../components/formHocWithFormName';
import { getSimpleDataForm, setAllErrorsInFormNamed } from '../../utils';
import { cleanBookingFormData, createAppointment, updateFormWithFormNameData } from './bookingFormSlice';
import { Button } from '../../components/Button/Button';
import { useWidgetProps } from '../../WidgetPropsContext';
import { selectIsBooked, selectSelectedBranch } from './selectors';
import { RECAPTCHA_KEY } from '../../config';
import { ageLimitsAreKept, getAgeOfPatient } from './utils';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import './form.scss';
import { Agreements } from '../../components/Agreements/Agreements';
import { InfoMessage } from '../../components/InfoMessage/InfoMessage';
import { PhoneList } from '../../components/PhoneList/PhoneList';
import { Warning } from '../../components/Warning/Warning';
import {
    getActiveTimeslotId,
    getChildrenState,
    getDoctors,
    getSelectedDate,
    getSelectedDoctorAgeLimits,
    getSelectedDoctorAssignment,
    getSelectedService,
} from '../../serviceBooking/store/selectors';
import { fetchServicesTimetable, setActiveTimeslotId, setBookingError } from '../../serviceBooking/store/serviceSlice';
import { getTimetableInfo } from '../../serviceBooking/ServiceBookingContainer';
import { debugLog } from '../../serviceBooking/util/debug';
import { COOKIE_POLICY_URL } from '../../utils/constants';

const page = require('b_').with('page');
const form = require('b_').with('form');

const RECAPTCHA_ERROR_CLASS = 'input__group_recaptcha_error';
const RECAPTCHA_CLASS = 'input__group input__group_recaptcha';

const YOUR_DATA = 'Ваши данные';
const SEX = 'Пол';
const makeAppointmentButtonMod = { 'make-appointment': true };
const TIMESLOT_IS_OCCUPIED = 'Временной интервал уже занят.\n Пожалуйста, выберите другое время для визита.';
const SOMETHING_WENT_WRONG =
    'Вероятно, пропало интернет-соединение или сервис недоступен.\n Попробуйте еще раз или повторите попытку позже.';
const AGE_LIMITS_ISSUE = 'Онлайн-запись невозможна по возрастным ограничениям.\n Пожалуйста, выберите другого врача.';
const RESOURCE_BUSY_ISSUE = 'К сожалению, данный временной интервал недоступен для выбранной услуги';

// eslint-disable-next-line max-statements
export const BookingForm = formHocWithFormName(({ formData, formName }) => {
    const {
        baseUrl,
        isDoctoraOnline,
        serviceBooking,
        bookingEventName,
        bookingEventTarget,
        textBeforeBooking,
        textBeforeBookingClass,
        consentsEnabled,
    } = useWidgetProps();
    const history = useHistory();
    const { selected, serviceName } = useSelector(getSelectedService);
    const { forChild, childrenAge: age } = useSelector(getChildrenState);
    const { timeslotId } = useParams();
    const serviceTimeslotId = useSelector(getActiveTimeslotId);
    const serviceAssignmentId = useSelector(getSelectedDoctorAssignment);
    const doctorAgeLimits = useSelector(getSelectedDoctorAgeLimits);
    const doctorsServices = useSelector(getDoctors);
    const lastBirthday = useRef(null);

    const locationState = useLocation();
    const serviceState = {
        state: {
            doctorId: serviceAssignmentId,
            adult: !forChild,
            children: forChild,
            childrenAge: age,
            online: true,
        },
    };
    const {
        state, state: {
            doctorId, online, adult, adultAge, adultAgeTo, children, childrenAge, childrenAgeTo,
        } = {},
    } =
        !serviceBooking ? locationState : serviceState;

    const dispatch = useDispatch();
    const { isLoading, isBooked, error } = useSelector(selectIsBooked);
    const branch = serviceBooking ? selected?.branches : useSelector(selectSelectedBranch);
    const date = useSelector(getSelectedDate);
    const recaptchaRef = useRef();

    const [ isAgreementsConfirm, setIsAgreementsConfirm ] = useState(false);
    const [ isChildPatient, setIsChildPatient ] = useState(false);
    const onChange = value => setIsAgreementsConfirm(value);

    const {
        firstName, lastName, middleName, birthDate, sex, phone, email, clientNote,
    } = formData[formName].fields;

    useEffect(() => {
        const patientData = getSimpleDataForm(formData[formName].fields);

        isBooked &&
        history.replace('/successfully-booked', {
            ...state,
            timeslotId,
            branch,
            patientData,
        });
    }, [ isBooked, timeslotId, state, branch, formData, formName ]);

    useEffect(() => {
        if (lastBirthday.current && lastBirthday?.current !== birthDate?.value) {
            dispatch(setBookingError(''));
        }

        lastBirthday.current = birthDate?.value;
    }, [birthDate?.value]);

    useEffect(() => {
        if (birthDate?.value) {
            const patientAge = getAgeOfPatient(birthDate.value);
            setIsChildPatient(patientAge < 18);
        } else {
            setIsChildPatient(false);
        }
    }, [birthDate?.value]);

    useEffect(() => {
        if (error && !isBooked) {
            const patientData = getSimpleDataForm(formData[formName].fields);
            // eslint-disable-next-line no-undef
            let errorMessage = TIMESLOT_IS_OCCUPIED;
            // eslint-disable-next-line no-undef
            if (navigator?.onLine === false || error?.message === 'Rejected') {
                errorMessage = SOMETHING_WENT_WRONG;
            } else if (error?.code === 'RESOURCE_BUSY') {
                errorMessage = RESOURCE_BUSY_ISSUE;
            }
            serviceBooking && dispatch(setBookingError(errorMessage));
            history.replace(`/${serviceBooking ? '' : 'error'}`, {
                patientData,
                errorMessage,
                reloadBranches: false,
                isTimeslotOccupied: true,
            });
            if (serviceBooking) {
                dispatch(cleanBookingFormData());
                dispatch(setActiveTimeslotId(''));

                const doc = doctorsServices?.find(({ doctorId }) => doctorId === selected?.doctors);
                if (!doc) {
                    return;
                }
                (async () => {
                    try {
                        const info = await getTimetableInfo(doc, selected.branches, selected?.doctors);
                        debugLog('Info for timetable', info);
                        if (info?.assignment) {
                            dispatch(
                                fetchServicesTimetable({
                                    baseUrl,
                                    ...info,
                                }),
                            );
                        }
                    } catch (e) {
                        // console.log('Error async', e);
                    }
                })();
            }
        }
    }, [ error, isBooked ]);

    const sendBookingEvent = () => {
        if (bookingEventName && bookingEventTarget) {
            try {
                window?.parent?.postMessage?.({ event: bookingEventName }, bookingEventTarget);
                console.log(`booking event ${bookingEventName} send to  ${bookingEventTarget}`);
            } catch (e) {
                console.log('booking event error: ', e);
            }
        }
    };

    const onMakeAppointment = () => {
        const fieldsWithErrors = setAllErrorsInFormNamed(formData, formName);
        const recaptchaValue = recaptchaRef.current.getValue();
        const recaptchaNode = findDOMNode(recaptchaRef.current);
        const { isFormValid } = formData[formName];
        const patientData = getSimpleDataForm(formData[formName].fields);
        const addParam = serviceBooking
            ? {
                serviceId: selected?.services,
                slotDuration: selected?.slotDuration,
                serviceName,
            }
            : {};
        const bodyForRequest = {
            doctorId,
            timeslotId: serviceBooking ? serviceTimeslotId : timeslotId,
            body: {
                ...patientData,
                recaptchaValue,
                ...(consentsEnabled === true && isAgreementsConfirm ? { onlineBookingConsent: true } : {}),
                ...addParam,
            },
            baseUrl,
            isDoctoraOnline,
        };

        if (fieldsWithErrors || !recaptchaValue) {
            recaptchaNode.classList.toggle(RECAPTCHA_ERROR_CLASS, !recaptchaValue);
            return dispatch(updateFormWithFormNameData(fieldsWithErrors ?? { data: {} }));
        }

        if (
            !ageLimitsAreKept({
                patientAge: getAgeOfPatient(birthDate.value),
                isDoctorTreatAdults: serviceBooking ? doctorAgeLimits?.adult : adult,
                isDoctorTreatChildren: serviceBooking ? doctorAgeLimits?.children : children,
                childrenAge: serviceBooking ? doctorAgeLimits?.childrenAge : childrenAge,
                adultAge: serviceBooking ? doctorAgeLimits?.adultAge : adultAge,
                adultAgeTo: serviceBooking ? doctorAgeLimits?.adultAgeTo : adultAgeTo,
                childrenAgeTo: serviceBooking ? doctorAgeLimits?.childrenAgeTo : childrenAgeTo,
            })
        ) {
            if (serviceBooking) {
                dispatch(setBookingError(AGE_LIMITS_ISSUE));
                dispatch(cleanBookingFormData());
                dispatch(setActiveTimeslotId(''));
            }

            return history.replace(`/${serviceBooking ? '' : 'error'}`, {
                ...state,
                patientData,
                errorMessage: AGE_LIMITS_ISSUE,
                reloadBranches: false,
                isTimeslotOccupied: false,
            });
        }
        sendBookingEvent();
        return isFormValid && dispatch(createAppointment(bodyForRequest));
    };

    const onRecaptcha = (value) => {
        const recaptchaNode = findDOMNode(recaptchaRef.current);
        recaptchaNode.classList.toggle(RECAPTCHA_ERROR_CLASS, !value);
    };

    return (
        <section className={page('section')}>
            {isLoading ? <LoadingSpinner/> : null}
            {online ? (
                <div className={form({ 'make-appointment': true })}>
                    <FormTitle title={YOUR_DATA}/>
                    <GroupInput {...lastName} />
                    <GroupInput {...firstName} />
                    <GroupInput {...middleName} />
                    <div className={form('box')}>
                        <GroupInput {...birthDate} />

                        <FormGroup title={SEX} required>
                            <GroupInput {...sex} wrapperMod={{ sex: true }}/>
                        </FormGroup>
                    </div>
                    <GroupInput {...phone} />
                    <GroupInput {...email} />
                    <GroupInput wrapperMod={{ clientNote: true }} {...clientNote} />
                    <Agreements onChange={onChange} isChild={isChildPatient}/>
                    {!RECAPTCHA_KEY ? null : (
                        <ReCAPTCHA className={RECAPTCHA_CLASS} ref={recaptchaRef} sitekey={RECAPTCHA_KEY}
                            onChange={onRecaptcha}/>
                    )}
                    {!!textBeforeBooking && (
                        <div
                            className={textBeforeBookingClass || ''}
                            style={{ fontSize: 14, lineHeight: '22px', marginTop: '24px' }}
                        >
                            {textBeforeBooking}
                        </div>
                    )}
                    {consentsEnabled === true && (
                        <div style={{ marginTop: 20 }}>
                            <Link href={`https://${DOC_DOMAIN}/privacy`} title="Политика конфиденциальности"/>
                            <div style={{ marginTop: 8, fontSize: 14, lineHeight: '22px' }}>
                                Мы используем файлы cookies, с политикой их обработки можно ознакомится{' '}
                                <Link href={COOKIE_POLICY_URL} title="здесь"/>.
                            </div>
                        </div>
                    )}
                    {state?.errorMessage && (
                        <div style={{ paddingTop: 20 }}>
                            <Warning text={state?.errorMessage} show/>
                        </div>
                    )}
                    <Button
                        mod={makeAppointmentButtonMod}
                        onClick={onMakeAppointment}
                        title={'Записаться'}
                        disabled={!isAgreementsConfirm}
                    />
                </div>
            ) : (
                <div>
                    <InfoMessage icon="warning">
                        Онлайн-запись к этому врачу ограничена медцентром. Вы можете выбрать время и записаться через
                        звонок в
                        регистратуру.
                    </InfoMessage>
                    <PhoneList branch={branch} label="Номера регистратуры"/>
                </div>
            )}
        </section>
    );
});
